import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Divider, Grid, IconButton, Paper, Typography, useTheme } from '@mui/material';
import { Clear, Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountData, SchedulerConfigurationData } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { calculateResourceSelection, isValidStartingPosition } from './resourceSelectionUtils';

enum ResourceState {
  PRIMARY = 'primary',
  SELECTED = 'selected',
  AVAILABLE = 'available',
  INVALID = 'invalid',
}

type ResourceSelectionPanelProps = {
  orderPageId: string;
  persons: number;
  selectedResourceIds: number[];
  setSelectedResourceIds: (ids: number[]) => void;
  disabled?: boolean;
};

interface AvailableResource {
  id: string;
  index: number; // 0-based index
  displayName: string;
  schedulerConfigId: string;
  config: SchedulerConfigurationData;
}

export const ResourceSelectionPanel: React.FC<ResourceSelectionPanelProps> = ({
  orderPageId,
  persons,
  selectedResourceIds,
  setSelectedResourceIds,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const [primaryResourceIndex, setPrimaryResourceIndex] = useState<number | null>(null);

  // Get scheduler configurations applicable to this order page
  const applicableConfigs = useMemo(() => {
    const schedulerConfigs = account?.shared_resources?.scheduler_configs || [];
    return schedulerConfigs.filter((config) =>
      config.allowed_masks?.some((mask) => mask.order_page_id === orderPageId),
    );
  }, [account, orderPageId]);

  // Generate available resources from applicable configs
  const availableResources = useMemo((): AvailableResource[] => {
    const resources: AvailableResource[] = [];

    applicableConfigs.forEach((config: SchedulerConfigurationData) => {
      for (let i = 0; i < config.resource_count; i++) {
        resources.push({
          id: `${config.id}-${i}`,
          index: i,
          displayName: `${(config.label as any)[language] || (config.label as any).en || 'Resource'} ${i + 1}`,
          schedulerConfigId: config.id,
          config,
        });
      }
    });

    return resources;
  }, [applicableConfigs, language]);

  // Get the primary config (assuming single config per order page for now)
  const primaryConfig = applicableConfigs[0];

  const resourcesNeeded = primaryConfig ? Math.ceil(persons / primaryConfig.persons_per_resource) : 0;

  // Handle primary resource selection
  const handleResourceClick = (resource: AvailableResource) => {
    if (disabled) return;

    const availableIndexes = availableResources
      .filter((r) => r.schedulerConfigId === resource.schedulerConfigId)
      .map((r) => r.index);

    const calculation = calculateResourceSelection(resource.index, persons, resource.config, availableIndexes);

    if (calculation.isValid && calculation.selectedResources) {
      setPrimaryResourceIndex(resource.index);
      setSelectedResourceIds(calculation.selectedResources);
    }
  };

  // Handle manual resource toggle (for non-adjacent configurations)
  const handleResourceToggle = (resource: AvailableResource) => {
    if (disabled || !primaryConfig || primaryConfig.require_adjacent_resources) {
      return;
    }

    const isSelected = selectedResourceIds.includes(resource.index);
    let newSelection: number[];

    if (isSelected) {
      newSelection = selectedResourceIds.filter((id) => id !== resource.index);
    } else if (selectedResourceIds.length < resourcesNeeded) {
      newSelection = [...selectedResourceIds, resource.index];
    } else {
      return; // Cannot select more than needed
    }

    setSelectedResourceIds(newSelection);

    // Update primary if needed
    if (newSelection.length > 0 && !newSelection.includes(primaryResourceIndex || -1)) {
      setPrimaryResourceIndex(newSelection[0]);
    }
  };

  const handleClearSelection = () => {
    if (disabled) return;
    setSelectedResourceIds([]);
    setPrimaryResourceIndex(null);
  };

  // Get resource selection state
  const getResourceState = (resource: AvailableResource): ResourceState => {
    if (resource.index === primaryResourceIndex) return ResourceState.PRIMARY;
    if (selectedResourceIds.includes(resource.index)) return ResourceState.SELECTED;

    // Check if this resource could be a valid starting point
    if (primaryConfig && persons > 0) {
      const availableIndexes = availableResources
        .filter((r) => r.schedulerConfigId === resource.schedulerConfigId)
        .map((r) => r.index);

      const wouldBeValid = isValidStartingPosition(resource.index, persons, resource.config, availableIndexes);

      return wouldBeValid ? ResourceState.AVAILABLE : ResourceState.INVALID;
    }

    return ResourceState.AVAILABLE;
  };

  const getResourceButtonColor = (state: ResourceState) => {
    switch (state) {
      case ResourceState.PRIMARY:
        return theme.palette.primary.main;
      case ResourceState.SELECTED:
        return theme.palette.primary.light;
      case ResourceState.AVAILABLE:
        return theme.palette.grey[300];
      case ResourceState.INVALID:
        return theme.palette.grey[200];
      default:
        return theme.palette.grey[300];
    }
  };

  // Don't render if no applicable configs or no persons
  if (applicableConfigs.length === 0 || persons === 0) {
    return null;
  }

  return (
    <Box>
      <Grid container alignItems="center" gap={1} mb={2}>
        <Typography variant="subtitle2" fontWeight={600}>
          {t('calendar.edit_order.resourceSelection.title')}
        </Typography>
        <Chip
          size="small"
          icon={<Info />}
          label={t('calendar.edit_order.resourceSelection.needsResources', {
            count: resourcesNeeded,
            persons,
          })}
          variant="outlined"
        />
      </Grid>

      {/* Resource Grid */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1}>
          {availableResources.map((resource) => {
            const state = getResourceState(resource);
            const isDisabled = disabled || state === ResourceState.INVALID;

            return (
              <Grid item key={resource.id}>
                <Button
                  variant={
                    state === ResourceState.PRIMARY || state === ResourceState.SELECTED ? 'contained' : 'outlined'
                  }
                  size="small"
                  disabled={isDisabled}
                  onClick={() =>
                    primaryConfig.require_adjacent_resources
                      ? handleResourceClick(resource)
                      : handleResourceToggle(resource)
                  }
                  sx={{
                    minWidth: 60,
                    backgroundColor: getResourceButtonColor(state),
                    borderColor: getResourceButtonColor(state),
                    '&:hover': {
                      backgroundColor: isDisabled ? undefined : theme.palette.primary.dark,
                    },
                  }}>
                  {resource.index + 1}
                  {state === ResourceState.PRIMARY && (
                    <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.6rem' }}>
                      ★
                    </Typography>
                  )}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Selection Info and Actions */}
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          {selectedResourceIds.length > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('calendar.edit_order.resourceSelection.selected', {
                resources: selectedResourceIds.map((id) => id + 1).join(', '),
                count: selectedResourceIds.length,
              })}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('calendar.edit_order.resourceSelection.autoAssign')}
            </Typography>
          )}
        </Grid>

        {selectedResourceIds.length > 0 && (
          <Grid item>
            <IconButton
              size="small"
              onClick={handleClearSelection}
              disabled={disabled}
              title={t('calendar.edit_order.resourceSelection.clearSelection')}>
              <Clear fontSize="small" />
            </IconButton>
          </Grid>
        )}
      </Grid>

      {primaryConfig?.require_adjacent_resources && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('calendar.edit_order.resourceSelection.adjacentRequired')}
          </Typography>
          <Divider sx={{ mt: 1 }} />
        </>
      )}
    </Box>
  );
};
